import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Heart, Leaf, Users, Check } from 'lucide-react';
import { businessInfo, images } from '../data/mock';

const AboutPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#F7F8FA] via-white to-[#AF9EB8]/10">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
                About Us
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-medium text-[#2C2F36] mb-6">
                Your Partner in Natural Wellness
              </h1>
              <p className="text-lg text-[#4A5568] leading-relaxed">
                At Serenity Natural Health & Wellness, we are dedicated to helping you achieve 
                optimal health through a personalized, natural approach. Our mission is to support 
                your body's innate healing abilities with nutritional therapy and holistic care.
              </p>
            </div>
            <div className="relative">
              <img 
                src={images.owner2}
                alt="Julie Hilliker - Traditional Naturopath"
                className="rounded-2xl shadow-xl w-full max-w-md mx-auto"
              />
              <div className="absolute -bottom-4 -right-4 w-full h-full max-w-md mx-auto border-2 border-[#6E9EB9]/30 rounded-2xl hidden lg:block" />
            </div>
          </div>
        </div>
      </section>

      {/* Meet Julie Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={images.owner1}
                alt="Julie Hilliker"
                className="rounded-2xl shadow-lg w-full max-w-md mx-auto"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-1 bg-[#AF9EB8]/10 rounded-full text-sm font-medium text-[#AF9EB8] mb-4">
                Meet Your Practitioner
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
                {businessInfo.owner.name}, {businessInfo.owner.credentials}
              </h2>
              <p className="text-[#6E9EB9] font-medium mb-6">
                {businessInfo.owner.title}
              </p>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                {businessInfo.owner.bio}
              </p>
              <p className="text-[#4A5568] mb-8 leading-relaxed">
                With years of experience in Traditional Naturopathy, Julie has helped countless 
                individuals restore their health through personalized nutritional protocols, 
                herbal remedies, and comprehensive detoxification support.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-[#F7F8FA] rounded-full">
                  <Award size={18} className="text-[#6E9EB9]" />
                  <span className="text-sm font-medium text-[#2C2F36]">ANMA Board Certified</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-[#F7F8FA] rounded-full">
                  <Award size={18} className="text-[#6E9EB9]" />
                  <span className="text-sm font-medium text-[#2C2F36]">Licensed Massage Therapist</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Traditional Naturopathy */}
      <section className="section-padding bg-[#F7F8FA]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
                Our Approach
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
                What is Traditional Naturopathy?
              </h2>
              <div className="floral-divider w-24 mx-auto mb-6" />
            </div>
            
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm">
              <p className="text-[#4A5568] mb-6 leading-relaxed text-lg">
                Traditional Naturopathy is a holistic health discipline that focuses on supporting 
                the body's natural ability to heal itself. Rather than treating symptoms in isolation, 
                we look at the whole person — body, mind, and lifestyle — to identify the root causes 
                of health imbalances.
              </p>
              <p className="text-[#4A5568] mb-8 leading-relaxed text-lg">
                Our approach emphasizes prevention and education, empowering you with the knowledge 
                and tools to take control of your health. We believe that given the right support, 
                your body has an incredible capacity for healing and restoration.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#6E9EB9]/10 rounded-full flex-shrink-0">
                    <Leaf size={24} className="text-[#6E9EB9]" />
                  </div>
                  <div>
                    <h4 className="font-serif text-lg font-medium text-[#2C2F36] mb-2">Natural Remedies</h4>
                    <p className="text-[#4A5568]">We use nutrition, herbs, and natural therapies to support healing.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#AF9EB8]/10 rounded-full flex-shrink-0">
                    <Heart size={24} className="text-[#AF9EB8]" />
                  </div>
                  <div>
                    <h4 className="font-serif text-lg font-medium text-[#2C2F36] mb-2">Whole-Person Care</h4>
                    <p className="text-[#4A5568]">We consider all aspects of your health and lifestyle.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#93A8C7]/10 rounded-full flex-shrink-0">
                    <Users size={24} className="text-[#93A8C7]" />
                  </div>
                  <div>
                    <h4 className="font-serif text-lg font-medium text-[#2C2F36] mb-2">Individualized Approach</h4>
                    <p className="text-[#4A5568]">Every protocol is tailored to your unique needs.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#A08CA8]/10 rounded-full flex-shrink-0">
                    <Award size={24} className="text-[#A08CA8]" />
                  </div>
                  <div>
                    <h4 className="font-serif text-lg font-medium text-[#2C2F36] mb-2">Education & Empowerment</h4>
                    <p className="text-[#4A5568]">We teach you how to maintain optimal health long-term.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#AF9EB8]/10 rounded-full text-sm font-medium text-[#AF9EB8] mb-4">
              Our Philosophy
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              How We Evaluate, Determine, and Educate
            </h2>
            <div className="floral-divider w-24 mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-[#6E9EB9]/5 to-white border border-[#6E9EB9]/10">
              <div className="w-16 h-16 bg-[#6E9EB9]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-serif font-bold text-[#6E9EB9]">1</span>
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2C2F36] mb-4">How We Evaluate</h3>
              <p className="text-[#4A5568]">
                We conduct comprehensive assessments of your health history, current concerns, 
                lifestyle factors, and wellness goals to understand your unique situation.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-[#AF9EB8]/5 to-white border border-[#AF9EB8]/10">
              <div className="w-16 h-16 bg-[#AF9EB8]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-serif font-bold text-[#AF9EB8]">2</span>
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2C2F36] mb-4">Help You Determine</h3>
              <p className="text-[#4A5568]">
                Together, we identify the root causes of your health concerns and develop a 
                personalized protocol that addresses your specific needs.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-[#93A8C7]/5 to-white border border-[#93A8C7]/10">
              <div className="w-16 h-16 bg-[#93A8C7]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-serif font-bold text-[#93A8C7]">3</span>
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2C2F36] mb-4">Get Well & Educate</h3>
              <p className="text-[#4A5568]">
                We provide ongoing support and education, empowering you with the knowledge 
                to maintain your health and make informed wellness decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Clinic */}
      <section className="section-padding bg-[#F7F8FA]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
              Our Space
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              A Welcoming Environment for Healing
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Our clinic in Gordon, Nebraska is designed to be a peaceful sanctuary where you can 
              focus on your wellness journey. We've created a warm, inviting space that promotes 
              relaxation and healing.
            </p>
          </div>

          {/* Image Gallery */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={images.wellnessArea1}
                alt="Main Wellness Area"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={images.wellnessArea2}
                alt="Class Space"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={images.consultationArea}
                alt="Consultation Area"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={images.cryotherapyRoom}
                alt="Cryotherapy Room"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={images.infraredSauna}
                alt="Infrared Sauna"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={images.cherryBlossom}
                alt="Cherry Blossom Decor"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-serif text-2xl font-medium text-[#2C2F36] mb-4">
                  What You'll Find at Our Clinic
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check size={20} className="text-[#6E9EB9] mt-0.5" />
                    <span className="text-[#4A5568]">Comfortable consultation rooms for private sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check size={20} className="text-[#6E9EB9] mt-0.5" />
                    <span className="text-[#4A5568]">Dedicated wellness therapy area with cryotherapy and sauna</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check size={20} className="text-[#6E9EB9] mt-0.5" />
                    <span className="text-[#4A5568]">Class space for yoga, Zumba, and educational programs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check size={20} className="text-[#6E9EB9] mt-0.5" />
                    <span className="text-[#4A5568]">High-quality supplements and herbal products available</span>
                  </li>
                </ul>
              </div>
              <div className="rounded-xl overflow-hidden shadow-md">
                <img 
                  src={images.dogOnChair}
                  alt="Relaxing atmosphere at Serenity"
                  className="w-full h-64 object-cover"
                />
                <p className="text-center py-3 text-sm text-[#718096] bg-gray-50">Even our furry friends feel at home!</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/contact" className="btn-primary">
              Schedule a Visit
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#6E9EB9] to-[#93A8C7] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">
            Ready to Experience Natural Wellness?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8">
            Start with a free orientation class to learn about our approach, or schedule 
            your personalized consultation with Julie today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-white text-[#6E9EB9] rounded-full font-semibold hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
            >
              Book Consultation
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/orientation" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              Free Orientation Class
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;